import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useCollapse } from '../../hooks';
import classNames from 'classnames';
import { InfoCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { Typography, Tooltip, Button, Dropdown, ConfirmModal, SwitchSummaryViewMode } from '../../components';
import { SUMMARY_SECTION_TOOLTIP } from '../../constants/discovery';
import { exportSummaryAsCsv, exportSummaryAsPdf } from './DiscoverySummaryExportHelpers';
import { useInventorySummaryData } from './useInventorySummaryData';
import { InventoryCardsSection } from './InventoryCardsSection';
import { SummaryDetailsSection } from './SummaryDetailsSection';
import { SummaryEmptyState } from './SummaryEmptyState';

import './SummaryWrapper.scss';

const { Text, Title, Paragraph } = Typography;

export const SummaryWrapper = ({
    title,
    className = '',
    setDashboardHasNoData = () => {},
    canRenderImportButton = false,
    isWaitingForUpload = false,
    showCardEllipsis = true,
    showMarkReadyInfo = true,
    showViewModeToggle = true,
    collapsible = true,
    onFilterClick,
    ...props
}) => {
    const summaryWrapperRef = useRef(null);
    const [collapsed, toggleCollapsed] = useCollapse(false);
    const [showIgnoreOptimizationWarningModal, setShowIgnoreOptimizationWarningModal] = useState(false);
    const [optimizationAction, setOptimizationAction] = useState(null);

    const handleConfirmModal = useCallback((optimization) => {
        setShowIgnoreOptimizationWarningModal(true);
        setOptimizationAction(optimization);
    }, []);

    const {
        summaryData,
        isLoading,
        isError,
        isDataReady,
        inventoryTotals,
        hasNoData,
        handleIgnoreOptimizations: runIgnoreOptimizations,
    } = useInventorySummaryData({
        setDashboardHasNoData,
        canRenderImportButton,
    });

    const loadingOrNoData = isLoading || !isDataReady;

    const queryState = useMemo(() => ({ isLoading: loadingOrNoData, isError }), [loadingOrNoData, isError]);

    const handleIgnoreConfirmed = async () => {
        const success = await runIgnoreOptimizations(optimizationAction);
        if (success) {
            setOptimizationAction(null);
            setShowIgnoreOptimizationWarningModal(false);
        }
    };

    const exportDropdownItems = useMemo(
        () => [
            { key: 'csv', label: 'Export as CSV', onClick: () => exportSummaryAsCsv(summaryData) },
            { key: 'pdf', label: 'Export as PDF', onClick: () => exportSummaryAsPdf(summaryWrapperRef.current) },
        ],
        [summaryData],
    );

    const isViewModeDisabled = loadingOrNoData || hasNoData || isWaitingForUpload;

    return (
        <>
            {hasNoData ? (
                <SummaryEmptyState />
            ) : (
                <div
                    ref={summaryWrapperRef}
                    className={classNames('panel-wrapper', 'summary-wrapper', className, {
                        'is-collapsed': collapsed,
                    })}
                    {...props}
                >
                    <div
                        className={classNames('summary-wrapper-header', {
                            'summary-wrapper-header--clickable': collapsible,
                        })}
                        onClick={collapsible ? toggleCollapsed : undefined}
                        aria-expanded={collapsible ? !collapsed : undefined}
                    >
                        <div className="summary-title-and-tooltip">
                            <Title level={2} className="semi-bold summary-wrapper-title">
                                {title}
                            </Title>
                            <Tooltip
                                className="summary-tooltip"
                                classNames={{ root: 'summary-tooltip-content' }}
                                placement="right"
                                title={SUMMARY_SECTION_TOOLTIP}
                            >
                                <InfoCircleOutlined className="summary-tooltip-icon" />
                            </Tooltip>
                        </div>
                        <div className="summary-wrapper-header-right">
                            {showViewModeToggle && (
                                <div className="summary-wrapper-controls" onClick={(e) => e.stopPropagation()}>
                                    {collapsible && (
                                        <SwitchSummaryViewMode
                                            collapsed={collapsed}
                                            onChange={toggleCollapsed}
                                            disabled={isViewModeDisabled}
                                        />
                                    )}
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomRight"
                                        menu={{ items: exportDropdownItems }}
                                        disabled={loadingOrNoData}
                                    >
                                        <Button type="text" icon={<MoreOutlined />} />
                                    </Dropdown>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={classNames('summary-wrapper-body', { collapsed })}>
                        <div className="summary-wrapper-content">
                            <div className="inventory-cards-grid">
                                <InventoryCardsSection
                                    summaryData={summaryData}
                                    totalInventory={inventoryTotals.totalInventory}
                                    onFilterClick={onFilterClick}
                                    onIgnoreOptimizations={handleConfirmModal}
                                    queryState={queryState}
                                />
                            </div>
                            {!collapsed && (
                                <div className="summary-details-grid">
                                    <SummaryDetailsSection
                                        summaryData={summaryData}
                                        onIgnoreOptimizations={handleConfirmModal}
                                        queryState={queryState}
                                    />
                                </div>
                            )}
                            {!loadingOrNoData && !collapsed && showMarkReadyInfo && (
                                <Text className="small text-light">
                                    Machines marked as &ldquo;Ready&rdquo; are excluded from over- and under-provisioned
                                    metrics.
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmModal
                title="Notice"
                open={showIgnoreOptimizationWarningModal}
                onCancel={() => setShowIgnoreOptimizationWarningModal(false)}
                onContinue={handleIgnoreConfirmed}
            >
                <Paragraph>This action will ignore the current optimization type.</Paragraph>
                <Paragraph>
                    Some machines may still have other recommended optimizations that must be addressed before they can
                    be marked as ready for migration.
                </Paragraph>
            </ConfirmModal>
        </>
    );
};
